import React, { useEffect, useLayoutEffect, useRef } from 'react';
import type { Delta } from 'quill/core';
import { useQuill } from '../editor/QuillContext';
import QuillComponent from '../editor/QuillComponent';
import styles from './SearchInput.module.scss';

interface SearchInputProps {
  placeholder?: string;
  onChange?: (content: Delta) => void;
  onEnter?: () => void;
  initialContent?: Delta;
}

const SearchInput = ({
  placeholder,
  onChange,
  initialContent,
  onEnter,
}: SearchInputProps) => {
  const { quill } = useQuill();
  const handlerRef = useRef({ onEnter });

  useLayoutEffect(() => {
    handlerRef.current = { onEnter };
  }, [onEnter]);

  useEffect(() => {
    if (!quill) return undefined;

    function onKeyDown(e: KeyboardEvent) {
      if (quill?.hasFocus()) {
        if (e.key === 'Escape') {
          quill.blur();
        }

        if (e.key === 'Enter') {
          handlerRef.current.onEnter?.();
        }
      }
    }

    quill.root.addEventListener('keydown', onKeyDown);

    return () => {
      quill.root.removeEventListener('keydown', onKeyDown);
    };
  }, [quill]);

  return (
    <div className={styles.searchInput}>
      <QuillComponent
        moduleConfig={{
          table: null,
          prefixListener: {
            persistOnSelectionChange: true,
          },
          keyboard: {
            bindings: {
              tab: {
                key: 'Tab',
                handler() {
                  return false;
                },
              },
              enter: {
                key: 'Enter',
                shiftKey: null,
                altKey: null,
                handler() {
                  return false;
                },
              },
            },
          },
        }}
        noTitleLine
        namespace="searchInput"
        theme="searchInputEditorTheme"
        readOnly={false}
        contentPlaceholder={placeholder}
        onTextChange={() => {
          if (quill) {
            onChange?.(quill.getContents());
          }
        }}
        initialContent={initialContent}
      />
    </div>
  );
};

export default SearchInput;
